import { ColumnDef } from "@tanstack/react-table";
import React, { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  CreateEntityTableCell,
  DateTableCell,
  PaginationControl,
  ReactTableComponent,
  RowSelectionControl,
  Status,
} from "../../../../components/ui-kit";
import { AppRoutes } from "../../../../constants/routes";
import { OrderApiType } from "../../../../util/types";
import { BillingNameColumn } from "../components/BillingNameColumn";
import { formatCurrency } from "../../../../util/data-utils";

const StyledReactTableComponent = styled(ReactTableComponent)`
  thead {
    tr {
      th {
        padding: 15px 10px;
        :first-child {
          padding: 0 0 0 10px;
        }
        :nth-child(2) {
          padding-left: 10px;
        }
        :last-child {
          padding-right: 6px;
        }
      }
    }
  }
`;

const Cell = styled.div`
  padding: 13px 10px;
`;

export interface OrdersTableProps {
  data: OrderApiType[];
  pagination: PaginationControl;
  rowSelection?: RowSelectionControl;
  refreshOrders: () => void;
  type: "full" | "customer";
}

export const OrdersTable: FC<OrdersTableProps> = ({
  data,
  pagination,
  rowSelection,
  refreshOrders,
  type = "full",
}) => {
  const navigate = useNavigate();

  const handleGoDetails = (row: OrderApiType) => {
    navigate(AppRoutes.Private.Orders.ORDERS_SHOW + "/" + row._id);
  };

  // END handlers

  const columns: Array<ColumnDef<OrderApiType, any>> = useMemo(
    () => [
      {
        header: "Order ID",
        meta: { mobileTitle: true },
        accessorKey: "reference",
        cell: ({ getValue }) => <Cell>{getValue()}</Cell>,
      },
      {
        header: "Customer",
        cell: ({
          row: {
            original: { customer },
          },
        }) => (
          <Cell>
            <BillingNameColumn
              name={customer?.name}
              firstName={customer?.firstName}
              lastName={customer?.lastName}
            />
          </Cell>
        ),
      },
      {
        header: "Date created",
        accessorKey: "createdAt",
        cell: ({ getValue }) => (
          <DateTableCell
            format={type === "customer" ? "dd LLL, yyyy" : undefined}
            date={getValue()}
          />
        ),
      },
      {
        header: "Total",
        accessorKey: "totalAmount",
        cell: ({ row: { original } }) => (
          <Cell>
            {formatCurrency(
              original.totalAmount.replaceAll(",", ""),
              2,
              original.currency
            )}
          </Cell>
        ),
      },
      {
        header: "Order status",
        accessorKey: "status",
        enableSorting: false,
        cell: ({ getValue }) => (
          <Cell>
            <Status status={getValue()} />
          </Cell>
        ),
      },
      {
        header: "Payment status",

        enableSorting: false,
        cell: ({ row: { original } }) => {
          return (
            <Cell>
              <Status status={original.payment?.status ?? "Unfulfilled"} />
            </Cell>
          );
        },
      },
      {
        header: "Created by",
        accessorKey: "createdBy",
        enableSorting: false,
        cell: (info) => {
          return (
            <CreateEntityTableCell
              createdByEntity={info.row.original.createdByEntity}
              createdBy={info.getValue()}
            />
          );
        },
      },
    ],
    [type]
  );

  return (
    <StyledReactTableComponent
      padding="0"
      data={data}
      columns={columns}
      withPagination={pagination}
      withRowSelection={rowSelection}
      onRowClick={handleGoDetails}
      withSorting
    />
  );
};
